// src/App.js
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import SiteLayout from "./layouts/SiteLayout";
import Home from "./pages/Home";
import ServicesPage from "./pages/Services";
import ProjectsPage from "./pages/Projects";
import CaseStudy from "./pages/CaseStudy";
import ProcessPage from "./pages/Process";
import AboutPage from "./pages/About";
import ContactPage from "./pages/Contact";
import NotFound from "./pages/NotFound";
import ApplicationsPage from "./pages/Applications";
import ApplicationDetailRouter from "./pages/applications/DetailRouter";
import ScrollToTop from "./components/ScrollToTop";
import ConsentBanner from "./consent/ConsentBanner";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import BackToTop from "./components/BackToTop";
import ReviewsPage from "./pages/Reviews"

const SUPPORTED = ["en", "ro"];
const FALLBACK = "en";

function RedirectToDetected() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const detected = (i18n.resolvedLanguage || i18n.language || "").slice(0, 2);
    const lng = SUPPORTED.includes(detected) ? detected : FALLBACK;
    navigate(`/${lng}`, { replace: true });
  }, [i18n, navigate]);

  return null;
}

function LanguageEnforcer({ children }) {
  const { i18n } = useTranslation();
  const { lng } = useParams();

  const invalid = !SUPPORTED.includes(lng);

  useEffect(() => {
    if (!invalid) {
      if (i18n.language !== lng) i18n.changeLanguage(lng);
      if (typeof document !== "undefined") document.documentElement.lang = lng;
    }
  }, [i18n, lng, invalid]);

  if (invalid) {
    return <Navigate to={`/${FALLBACK}`} replace />;
  }

  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop behavior="auto" />
      <Routes>
        {/* Redirect root to detected language */}
        <Route path="/" element={<RedirectToDetected />} />

        {/* Home */}
        <Route
          path="/:lng"
          element={
            <LanguageEnforcer>
              <SiteLayout>
                <Home />
              </SiteLayout>
            </LanguageEnforcer>
          }
        />

        {/* Applications (catalog) — keep your existing '/applications' spelling */}
        <Route
          path="/:lng/applications"
          element={
            <LanguageEnforcer>
              <SiteLayout>
                <ApplicationsPage />
              </SiteLayout>
            </LanguageEnforcer>
          }
        />

        {/* Applications (detail) */}
        <Route
          path="/:lng/applications/:slug"
          element={
            <LanguageEnforcer>
              <SiteLayout>
                <ApplicationDetailRouter />
              </SiteLayout>
            </LanguageEnforcer>
          }
        />

        {/* Services */}
        <Route
          path="/:lng/services"
          element={
            <LanguageEnforcer>
              <SiteLayout>
                <ServicesPage />
              </SiteLayout>
            </LanguageEnforcer>
          }
        />

        {/* Projects */}
        <Route
          path="/:lng/projects"
          element={
            <LanguageEnforcer>
              <SiteLayout>
                <ProjectsPage />
              </SiteLayout>
            </LanguageEnforcer>
          }
        />

        {/* One case study */}
        <Route
          path="/:lng/projects/:slug"
          element={
            <LanguageEnforcer>
              <SiteLayout>
                <CaseStudy />
              </SiteLayout>
            </LanguageEnforcer>
          }
        />

        {/* Process */}
        <Route
          path="/:lng/process"
          element={
            <LanguageEnforcer>
              <SiteLayout>
                <ProcessPage />
              </SiteLayout>
            </LanguageEnforcer>
          }
        />

        {/* About */}
        <Route
          path="/:lng/about"
          element={
            <LanguageEnforcer>
              <SiteLayout>
                <AboutPage />
              </SiteLayout>
            </LanguageEnforcer>
          }
        />

        {/* Contact */}
        <Route
          path="/:lng/contact"
          element={
            <LanguageEnforcer>
              <SiteLayout>
                <ContactPage />
              </SiteLayout>
            </LanguageEnforcer>
          }
        />
        <Route
          path="/:lng/privacy"
          element={
            <LanguageEnforcer>
              <SiteLayout>
                <PrivacyPolicy />
              </SiteLayout>
            </LanguageEnforcer>
          }
        />
        <Route
          path="/:lng/cookies"
          element={
            <LanguageEnforcer>
              <SiteLayout>
                <CookiePolicy />
              </SiteLayout>
            </LanguageEnforcer>
          }
        />
        <Route
          path="/:lng/reviews"
          element={
            <LanguageEnforcer>
              <SiteLayout>
                <ReviewsPage />
              </SiteLayout>
            </LanguageEnforcer>
          }
        />
        {/* 404 */}
        <Route
          path="*"
          element={
            <SiteLayout>
              <NotFound />
            </SiteLayout>
          }
        />
      </Routes>
      <ConsentBanner />
      <BackToTop />
    </BrowserRouter>
  );
}
